<script setup lang="ts">
import { TransitionPresets } from '@vueuse/core'
import { ref, computed, watch, onMounted, useFormat, useTransition } from '#imports'

interface AnimatedNumberProps {
    value?: number | string
    countOnMount?: boolean
    decimals?: number
    delay?: number // in seconds
    digitCount?: boolean
    duration?: number // in seconds
    isMoney?: boolean
}

const props = withDefaults(defineProps<AnimatedNumberProps>(), {
    value: 0,
    countOnMount: true,
    decimals: 0,
    delay: 0.1,
    duration: 1.5,
    isMoney: true
})

const { asMoney } = useFormat()

// This ref holds the target value for the transition.
// It's initialized based on whether we animate on mount and the starting logic.
const valueForTransition = ref(0)

const initialTargetNumber = computed(() => Number(props.value))

// Initialize valueForTransition for the first animation or static display
if (props.countOnMount) {
    let startValue = 0
    // Set startValue to count from amount relative to the endValue if value is above 10.000
    if (initialTargetNumber.value / 10000 > 1 && initialTargetNumber.value > 0) {
        startValue = initialTargetNumber.value * 0.95
    }

    // If digitCount is true, we want to start from the largest zeroed digits.
    // For example, if value is 12345, we start from 10000.
    // This is useful for large numbers to create a more visually appealing transition.
    if (props.digitCount) {
        startValue = Number(toLargestZeroedDigits(Math.round(Number(props.value))).join(''))
    }

    valueForTransition.value = startValue
}
else {
    valueForTransition.value = initialTargetNumber.value
}

const transitionDurationMs = computed(() => props.duration * 1000)
const transitionDelayMs = computed(() => props.delay * 1000)

// useTransition will animate 'animatedDisplayValue' whenever 'valueForTransition' changes.
const animatedDisplayValue = useTransition(valueForTransition, {
    duration: transitionDurationMs,
    delay: transitionDelayMs,
    transition: TransitionPresets.easeOutExpo
})

function toLargestZeroedDigits(digitNumber: string | number) {
    const rounededDigits = Math.round(Number(digitNumber))
    const valArray = Array.from({ length: rounededDigits.toString().length })
    valArray[0] = 1
    valArray.fill(0, 1)

    return valArray
}

onMounted(() => {
    if (props.countOnMount) {
        // Trigger the animation from the calculated 'startValue' to the actual 'initialTargetNumber'.
        // The 'delay' configured in useTransition options will apply here.
        valueForTransition.value = initialTargetNumber.value
    }
    // If !props.countOnMount, valueForTransition is already initialTargetNumber,
    // so animatedDisplayValue will reflect it (no transition if values are the same initially).
})

// Watch for subsequent changes to the 'value' prop
watch(initialTargetNumber, (newTarget) => {
    // When props.value changes, update valueForTransition.
    // useTransition will animate from its current 'animatedDisplayValue' to the new target.
    valueForTransition.value = newTarget
})

const formattedValue = computed(() => {
    const numValue = Number(animatedDisplayValue.value)
    if (props.isMoney) {
        return asMoney(numValue, { decimals: props.decimals })
    }
    return numValue.toFixed(props.decimals)
})
</script>

<template>
    <div class="animated-number">
        {{ formattedValue }}
    </div>
</template>

<style lang="scss" scoped>
.animated-number {
    font-variant-numeric: tabular-nums;
}
</style>
